import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';

export interface AlertProps {
  title?: string;
  message: string;
  variant?: 'info' | 'success' | 'warning' | 'error';
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Alert component for displaying messages
 *
 * Variants:
 * - info: Blue alert (informational)
 * - success: Green alert (successful operation)
 * - warning: Amber alert (warning or caution)
 * - error: Red alert (error or critical issue)
 */
export const Alert: React.FC<AlertProps> = ({
  title,
  message,
  variant = 'info',
  dismissible = false,
  onDismiss,
  className,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = React.useState(true);

  if (!isVisible) return null;

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  const variantClasses = {
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900',
      border: 'border-blue-200 dark:border-blue-700',
      icon: 'text-blue-600 dark:text-blue-400',
      title: 'text-blue-900 dark:text-blue-100',
      message: 'text-blue-800 dark:text-blue-200',
    },
    success: {
      bg: 'bg-emerald-50 dark:bg-emerald-900',
      border: 'border-emerald-200 dark:border-emerald-700',
      icon: 'text-emerald-600 dark:text-emerald-400',
      title: 'text-emerald-900 dark:text-emerald-100',
      message: 'text-emerald-800 dark:text-emerald-200',
    },
    warning: {
      bg: 'bg-amber-50 dark:bg-amber-900',
      border: 'border-amber-200 dark:border-amber-700',
      icon: 'text-amber-600 dark:text-amber-400',
      title: 'text-amber-900 dark:text-amber-100',
      message: 'text-amber-800 dark:text-amber-200',
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900',
      border: 'border-red-200 dark:border-red-700',
      icon: 'text-red-600 dark:text-red-400',
      title: 'text-red-900 dark:text-red-100',
      message: 'text-red-800 dark:text-red-200',
    },
  };

  const variant_classes = variantClasses[variant];
  const iconName = variant === 'error' ? 'alertCircle' : variant === 'success' ? 'checkCircle' : 'alertCircle';

  return (
    <div
      className={`
        rounded-lg border ${variant_classes.bg} ${variant_classes.border}
        p-4 flex gap-4 ${className || ''}
      `}
      role="alert"
    >
      <Icon name={iconName} size="md" className={variant_classes.icon} />

      <div className="flex-1">
        {title && <h3 className={`font-semibold ${variant_classes.title}`}>{title}</h3>}
        <p className={variant_classes.message}>{message}</p>
      </div>

      {dismissible && (
        <button
          onClick={handleDismiss}
          className={`self-start flex-shrink-0 ${variant_classes.icon} hover:opacity-75 transition-opacity`}
          aria-label={t('accessibility.dismissAlert', { ns: 'common' })}
        >
          <Icon name="close" size="md" />
        </button>
      )}
    </div>
  );
};
